import { isConfigured } from '../config.js';
import { setSelectedTrack } from '../api.js';

// The track the homepage CTAs jump straight into. Skipping the play
// hub for these buttons makes "Play Demo" mean what it says — one
// click into a race-setup screen, not a router stop. If brands-hatch
// is ever removed from the manifest the click still resolves
// (race-setup will surface the missing-track error in-page).
const DEMO_TRACK_ID = 'brands-hatch';

// The home page takes over the viewport with its own fixed nav, so we
// hide the global topbar while it's mounted (see `.home-route` rules
// in styles.css). The render function returns a cleanup that runs on
// route change.

export function render(mountEl) {
  document.body.classList.add('home-route');

  // Image lives in web/homepage-images/ so dev and dist resolve the
  // same relative path. The <img onerror> swaps in a graphic fallback.
  const screenshotSrc = './homepage-images/BrandsHatchRace.jpg';
  const demoLabel = isConfigured() ? 'Now in playable demo' : 'Now in playable demo';

  mountEl.innerHTML = `
    <div class="home-root">
      <nav class="home-nav">
        <span class="home-brand">Apex Slayer <span class="sub">// Race Engineer</span></span>
        <div class="home-nav-spacer"></div>
        <a href="#features" class="home-nav-link">Features</a>
        <a href="#how" class="home-nav-link">How It Works</a>
        <a href="https://discord.gg/CFrXRATXY8" class="home-nav-link discord" target="_blank" rel="noopener noreferrer">Discord</a>
        <a href="#/race-setup" class="home-nav-link cta" data-demo>Play Demo</a>
      </nav>

      <section class="home-hero">
        <div class="home-hero-status">
          <div>BUILD <span class="v">v0.4.0</span></div>
          <div>STATUS <span class="v" data-status>RACING</span></div>
        </div>

        <div class="home-hero-meta">
          <span class="dot"></span>
          <span>Race Engineering Simulator</span>
          <span class="sep">·</span>
          <span>${demoLabel}</span>
        </div>

        <div class="home-moment">
          <p><span class="lap">LAP 23 OF 30.</span> Your driver's tires are gone. The leader's pulling 0.4 a lap. The safety car's a coin flip and your engineer is in your ear about fuel.</p>
          <p>Box now, you lose three places. Box later, you lose the race.</p>
          <p class="question">What's the call?</p>
        </div>

        <h1 class="home-h1">
          <span class="you">You are the</span>
          Race<br>
          <span class="accent">Engineer.</span>
        </h1>

        <p class="home-tagline">
          A motorsport manager built on physics-based racing. <strong>You make the strategy calls.</strong> The cars do the rest. Build the circuit, run the team, win the race or watch your perfect plan come apart in turn six.
        </p>

        <div class="home-cta">
          <a href="#/race-setup" class="home-btn" data-demo>Call the race <span class="arrow">→</span></a>
          <a href="#features" class="home-btn secondary">See how it works</a>
        </div>
      </section>

      <div class="home-screenshot-band">
        <div class="home-screenshot">
          <div class="home-screenshot-corner">Brands Hatch</div>
          <img src="${screenshotSrc}" alt="Apex Slayer in-game screenshot showing live race timing, telemetry panel, and pit stop in progress" onerror="this.style.display='none';this.nextElementSibling.style.display='flex'">
          <div class="home-screenshot-fallback" style="display:none">In-game screenshot</div>
        </div>
      </div>

      <section id="features" class="home-section">
        <div class="home-section-label">// What you get</div>
        <h2 class="home-section-title">Real circuits. Real strategy. <span class="muted">Real consequences.</span></h2>
        <p class="home-section-intro">
          Apex Slayer focuses on the part of motorsport management most sims skip past: the racing itself.
          Build a track in minutes, run a 30-lap stint in real-time, and see whether your strategy survives contact with the other cars.
        </p>

        <div class="home-features">
          <div class="home-feature reveal">
            <div class="home-feature-num">/ 01</div>
            <div class="home-feature-title">Build any <span class="accent">circuit.</span></div>
            <div class="home-feature-body">
              Draw a track, line the walls, lay the kerbs, set the pit lane, mark the sectors. Real circuits, fictional circuits, the parking lot near your house — if it has corners, you can race it.
            </div>
          </div>

          <div class="home-feature reveal reveal-delay-1">
            <div class="home-feature-num">/ 02</div>
            <div class="home-feature-title">Run a <span class="accent">real race.</span></div>
            <div class="home-feature-body">
              Up to 22 cars on track. Real-time physics, real-time decisions. Tires fade. Fuel burns off. Drivers make mistakes. A pit stop is a 4-second window where the wrong call costs you the race and the right one wins it.
            </div>
          </div>

          <div class="home-feature reveal reveal-delay-2">
            <div class="home-feature-num">/ 03</div>
            <div class="home-feature-title">Engineer the <span class="accent">strategy.</span></div>
            <div class="home-feature-body">
              Push or conserve. Cover or commit. Pit on lap 18 or hold for the safety car. Apex Slayer doesn't decide for you, the timing tower tells you what's happening, the telemetry tells you why, and the call is yours.
            </div>
          </div>

          <div class="home-feature reveal reveal-delay-3">
            <div class="home-feature-num">/ 04</div>
            <div class="home-feature-title">Watch it <span class="accent">unfold.</span></div>
            <div class="home-feature-body">
              Live timing tower. Sector splits. Per-car telemetry. Speed up the green-flag stints, slow down for the pivotal laps, pause whenever you want to think. The race plays out at full speed in the physics engine, what you see is what actually happened.
            </div>
          </div>
        </div>
      </section>

      <div class="home-anti-section">
        <div class="inner">
          <div>
            <div class="home-section-label">// What's not in here</div>
            <h2 class="home-section-title small">We left some things <span class="muted">out.</span></h2>
            <p class="home-anti-intro">
              Most motorsport managers bury the racing under three menus of administrative grind. Apex Slayer makes a different bet: that the people who love management sims actually love the races, and the rest is friction.
            </p>
            <div class="home-resolve">
              Maybe later. Down the road, if the racing feels great and you want more, we'll add depth. <strong>For now, the focus stays tight.</strong> If that's why you came, you're in the right place.
            </div>
          </div>
          <ul class="home-anti-list">
            <li><div><strong>No sponsorship objectives.</strong> Race for the points, not the logo placements.</div></li>
            <li><div><strong>No R&amp;D upgrade trees to grind.</strong> Cars are what they are. Drive the wheels off them.</div></li>
            <li><div><strong>No financial spreadsheets.</strong> Budget anxiety is a real thing. Save it for tax season.</div></li>
            <li><div><strong>No board meetings, no morale minigames.</strong> The drivers are doing fine.</div></li>
            <li><div><strong>No part-by-part component upgrades.</strong> Setup, not procurement.</div></li>
            <li><div><strong>No scripted crashes or fake drama.</strong> If something dramatic happens, physics caused it.</div></li>
          </ul>
        </div>
      </div>

      <section id="how" class="home-section">
        <div class="home-section-label">// How a session goes</div>
        <h2 class="home-section-title">From cold tarmac to <span class="muted">checkered flag.</span></h2>

        <div class="home-steps">
          <div class="home-step reveal">
            <div class="home-step-num">01</div>
            <div class="home-step-content">
              <h3>Create your own circuit</h3>
              <p>Open the track editor. Drop a satellite image. Trace the racing line, place the walls, paint the kerbs, set the pit lane. Five minutes for a quick layout, an hour for something serious.</p>
            </div>
            <div class="home-step-time">~5–60 min</div>
          </div>

          <div class="home-step reveal reveal-delay-1">
            <div class="home-step-num">02</div>
            <div class="home-step-content">
              <h3>Build the grid</h3>
              <p>Drivers, teams, liveries.</p>
            </div>
            <div class="home-step-time">~2 min</div>
          </div>

          <div class="home-step reveal reveal-delay-2">
            <div class="home-step-num">03</div>
            <div class="home-step-content">
              <h3>Run the race</h3>
              <p>Run it green-to-checkered in real time, or fast-forward the cruise laps and slow down for the pit windows.</p>
            </div>
            <div class="home-step-time">~5–30 min</div>
          </div>

          <div class="home-step reveal reveal-delay-3">
            <div class="home-step-num">04</div>
            <div class="home-step-content">
              <h3>Argue with the data</h3>
              <p>Why was your driver slower in sector 2? Why did the tires fall off ten laps early? Why did the undercut not work? Telemetry knows. So do you, now.</p>
            </div>
            <div class="home-step-time">forever</div>
          </div>
        </div>
      </section>

      <section class="home-final-cta" id="play">
        <h2>Call the <span class="accent">race.</span></h2>
        <p>The demo runs in your browser. No download. No sign-up. Just you, the cars, and however many laps to figure out whether you can call a race.</p>
        <a href="#/race-setup" class="home-btn" data-demo>Start your engine <span class="arrow">→</span></a>
      </section>

      <footer class="home-footer">
        <div>
          <span class="accent">Apex Slayer</span>
          · Race Engineer
          · A solo project, built lap by lap
        </div>
        <div class="links">
          <a href="#features">Features</a>
          <a href="#how">How It Works</a>
          <a href="#/play">Play</a>
          <a href="https://discord.gg/CFrXRATXY8" class="discord" target="_blank" rel="noopener noreferrer">Discord</a>
        </div>
      </footer>
    </div>
  `;

  // In-page anchor links (#features / #how / #play) need to scroll
  // smoothly without triggering the hash router. Anything starting
  // with `#/` is a real route and falls through to the router.
  // `data-demo` CTAs ("Play Demo", "Call the race", "Start your
  // engine") are pre-handled here: we set the selected track to the
  // demo fixture so race-setup lands on Brands Hatch instead of
  // bouncing through the play hub.
  const onAnchorClick = (e) => {
    const a = e.target.closest('a[href^="#"]');
    if (!a) return;
    if (a.hasAttribute('data-demo')) {
      setSelectedTrack(DEMO_TRACK_ID);
      return;
    }
    const href = a.getAttribute('href');
    if (!href || href === '#' || href.startsWith('#/')) return;
    const target = mountEl.querySelector(href);
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  mountEl.addEventListener('click', onAnchorClick);

  // Reveal-on-scroll. One-shot per element.
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -60px 0px' });
  mountEl.querySelectorAll('.reveal').forEach(el => observer.observe(el));

  // Cosmetic broadcast-graphics status cycler in the hero corner.
  const statusValues = [
    { label: 'RACING',     color: 'var(--success)' },
    { label: 'GREEN FLAG', color: 'var(--success)' },
    { label: 'GRID UP',    color: 'var(--warning)' },
    { label: 'BOXING',     color: 'var(--info)' },
    { label: 'CHECKERED',  color: 'var(--accent)' },
  ];
  let statusIdx = 0;
  const statusEl = mountEl.querySelector('[data-status]');
  if (statusEl) statusEl.style.color = 'var(--success)';
  const statusTimer = setInterval(() => {
    statusIdx = (statusIdx + 1) % statusValues.length;
    if (!statusEl) return;
    statusEl.textContent = statusValues[statusIdx].label;
    statusEl.style.color = statusValues[statusIdx].color;
  }, 4000);

  return () => {
    clearInterval(statusTimer);
    observer.disconnect();
    mountEl.removeEventListener('click', onAnchorClick);
    document.body.classList.remove('home-route');
  };
}
